import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime } from '../utils/dateUtils';

const RideCompletionPage = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [tipAmount, setTipAmount] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRideDetails();
  }, [rideId]);

  const fetchRideDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rides/${rideId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRide(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTipSelection = (percentage) => {
    // Calculate tip based on the base fare (not including processing fee)
    const amount = parseFloat(ride.total_fare) * percentage;
    setTipAmount(amount);
    setCustomTip('');
  };

  const handleCustomTipChange = (value) => {
    setCustomTip(value);
    const amount = parseFloat(value);
    if (!isNaN(amount) && amount >= 0) {
      setTipAmount(amount);
    } else {
      setTipAmount(null);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/${rideId}/complete`,
        {
          completed_by: 'rider',
          tip_amount: tipAmount || 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Success!
      navigate('/rider/dashboard', { 
        state: { 
          message: `Ride completed! ${tipAmount > 0 ? `$${tipAmount.toFixed(2)} tip added.` : ''} Thank you!` 
        } 
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to complete ride');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading ride details...</div>
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-red-600 mb-4">Error: {error || 'Ride not found'}</div>
          <button
            onClick={() => navigate('/rider/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Separate fare and total for proper display
  const baseFare = parseFloat(ride.total_fare);  // Fare only (for calculating tip percentages)
  const riderTotal = ride.rider_total ? parseFloat(ride.rider_total) : baseFare;  // Total including processing fee

  // Calculate tip processing fee using pass-through formula: tip / 0.971 - tip
  // This ensures driver receives exactly the tip amount after Stripe's cut
  const calculateTipFee = (tip) => tip > 0 ? Math.ceil((tip / 0.971 - tip) * 100) / 100 : 0;
  const tipProcessingFee = calculateTipFee(tipAmount || 0);
  const totalWithTip = riderTotal + (tipAmount || 0) + tipProcessingFee;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Complete Your Ride</h1>
              <p className="text-gray-600">with {ride.driver_name}</p>
            </div>
          </div>

          {/* Ride Details */}
          <div className="border-t border-gray-200 pt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Date & Time</span>
              <span className="font-medium">
                {formatDateTime(ride.pickup_time, 'full')}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-gray-600">Route</span>
              <div className="text-right">
                <p className="font-medium">{ride.pickup_address}</p>
                <p className="text-sm text-gray-500">to</p>
                <p className="font-medium">{ride.dropoff_address}</p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t">
              <span className="text-gray-600 font-medium">Ride Cost</span>
              <span className="text-xl font-bold text-gray-900">${riderTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Tip Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Add a Tip (Optional)</h2>
          <p className="text-gray-600 mb-6">Show your appreciation for great service</p>

          {/* Preset Tip Buttons */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <button
              onClick={() => handleTipSelection(0.15)}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === baseFare * 0.15
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">15%</div>
              <div className="text-sm text-gray-600">${(baseFare * 0.15).toFixed(2)}</div>
            </button>
            <button
              onClick={() => handleTipSelection(0.20)}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === baseFare * 0.20
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">20%</div>
              <div className="text-sm text-gray-600">${(baseFare * 0.20).toFixed(2)}</div>
            </button>
            <button
              onClick={() => handleTipSelection(0.25)}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === baseFare * 0.25
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">25%</div>
              <div className="text-sm text-gray-600">${(baseFare * 0.25).toFixed(2)}</div>
            </button>
            <button
              onClick={() => { setTipAmount(0); setCustomTip(''); }}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === 0
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">No Tip</div>
              <div className="text-sm text-gray-600">$0.00</div>
            </button>
          </div>

          {/* Custom Tip Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Tip Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={customTip}
                onChange={(e) => handleCustomTipChange(e.target.value)}
                placeholder="0.00"
                className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Total Summary */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-lg">Ride Cost</span>
            <span className="text-lg">${riderTotal.toFixed(2)}</span>
          </div>
          {tipAmount > 0 && (
            <>
              <div className="flex justify-between items-center mb-1">
                <span className="text-lg">Tip</span>
                <span className="text-lg">${tipAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center mb-2 text-sm text-white/70">
                <span>Stripe Tip Fee</span>
                <span>${tipProcessingFee.toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="border-t border-white/30 pt-3 mt-3">
            <div className="flex justify-between items-center">
              <span className="text-xl font-bold">Total</span>
              <span className="text-3xl font-bold">${totalWithTip.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
              submitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {submitting ? 'Processing...' : 'Complete Ride & Pay'}
          </button>
          <button
            onClick={() => navigate('/rider/dashboard')}
            disabled={submitting}
            className="w-full py-3 rounded-lg font-medium text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RideCompletionPage;
